/* @refresh reset */
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Cog6ToothIcon,
  EnvelopeOpenIcon,
} from "@heroicons/react/24/outline";
import { PMButton } from "../../utils/PMButton";
import {
  fetchUserPersonalMailAiMailFolders,
  fetchUserPersonalMailAiMessages,
  fetchUserPersonalMailAiSettings,
  patchUserPersonalMailAiSettings,
  postMicrosoftSendTestMail,
  postUserPersonalMailAiProcessMessages,
} from "../../../api/microsoft";
import { buildOutlookWebMailboxUrl } from "../../../utils/microsoftOwaMail";
import { CompanyAiMailboxSettingsPanel } from "./CompanyAiMailboxSettingsPanel";
import { CompanyAiMailboxMessageReaderDialog } from "./CompanyAiMailboxMessageReaderDialog";

const DEFAULT_MAIL_FOLDER_KEY = "wk:inbox";

const WELL_KNOWN_MAIL_FOLDER_KEYS = [
  "wk:inbox",
  "wk:archive",
  "wk:sentitems",
  "wk:deleteditems",
  "wk:junkemail",
  "wk:drafts",
  "wk:outbox",
];

function mailFolderKeyToApiParams(key) {
  const k = (key || DEFAULT_MAIL_FOLDER_KEY).trim();
  if (k.startsWith("id:")) {
    const graphId = k.slice(3).trim();
    return graphId ? { mailFolderId: graphId } : { wellKnownFolder: "inbox" };
  }
  if (k.startsWith("wk:")) {
    const seg = k.slice(3).trim().toLowerCase();
    return seg ? { wellKnownFolder: seg } : { wellKnownFolder: "inbox" };
  }
  return { wellKnownFolder: "inbox" };
}

/**
 * Personal mailbox AI: rules + routing + inbox (same behaviour as company shared mailboxes).
 * Shown only when API reports +can_manage_personal_mail_ai+ (admin or microsoft_personal_mail_ai role).
 */
export function UserPersonalMailAiSection({ microsoftStatus, onMailAiChanged }) {
  const { t } = useTranslation();
  const [mailbox, setMailbox] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [mailFolders, setMailFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedFolderKey, setSelectedFolderKey] = useState(DEFAULT_MAIL_FOLDER_KEY);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [readerTarget, setReaderTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const canUse = Boolean(
    microsoftStatus?.can_manage_personal_mail_ai && microsoftStatus?.connected
  );

  const loadSettings = useCallback(() => {
    if (!canUse) return;
    setLoadingSettings(true);
    fetchUserPersonalMailAiSettings()
      .then((data) => setMailbox(data?.mail_ai_settings || null))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 403) {
          setMailbox(null);
          toast.error(t("microsoft.user.personal_mail_ai.forbidden_toast"));
        } else {
          setMailbox(null);
          toast.error(t("microsoft.user.personal_mail_ai.load_failed"));
        }
      })
      .finally(() => setLoadingSettings(false));
  }, [canUse, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const loadMailFolders = async () => {
    setLoadingFolders(true);
    try {
      const data = await fetchUserPersonalMailAiMailFolders();
      const raw = data?.mail_folders || [];
      const rows = raw.map((f) => ({
        id: f.id,
        display_name: f.display_name ?? f.displayName ?? "(folder)",
        parent_folder_id: f.parent_folder_id ?? f.parentFolderId,
        child_folder_count: f.child_folder_count ?? f.childFolderCount ?? 0,
        depth: f.depth ?? 0,
      }));
      setMailFolders(rows);
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0] || t("microsoft.ai_mailboxes.folder.folders_failed");
      toast.error(msg);
      setMailFolders([]);
    } finally {
      setLoadingFolders(null);
    }
  };

  const loadMessages = useCallback(
    async (folderKeyOverride) => {
      const key = folderKeyOverride ?? selectedFolderKey;
      const folderOpts = mailFolderKeyToApiParams(key);
      setLoadingMessages(true);
      try {
        const data = await fetchUserPersonalMailAiMessages({
          top: 25,
          ...folderOpts,
        });
        setMessages(data?.messages || []);
        setSelectedMessageIds([]);
      } catch (err) {
        const msg =
          err?.response?.data?.errors?.[0] || t("microsoft.ai_mailboxes.messages_failed");
        toast.error(msg);
        setMessages([]);
      } finally {
        setLoadingMessages(false);
      }
    },
    [selectedFolderKey, t]
  );

  const toggleInbox = () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (microsoftStatus?.can_send_mail) {
      loadMailFolders();
      loadMessages();
    }
  };

  const mergeMailbox = (updated) => {
    if (!updated?.id) return;
    setMailbox((prev) => ({ ...prev, ...updated }));
    onMailAiChanged?.();
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const toggleMessageSelected = (messageId) => {
    if (!messageId) return;
    setSelectedMessageIds((cur) => {
      const has = cur.includes(messageId);
      return has ? cur.filter((x) => x !== messageId) : [...cur, messageId];
    });
  };

  const selectAllMessages = (msgs) => {
    if (!msgs?.length) return;
    setSelectedMessageIds(msgs.map((m) => m.id).filter(Boolean));
  };

  const summarizeProcessing = (inboxProcessing) => {
    const list = inboxProcessing?.messages || [];
    let forwardsSent = 0;
    let forwardsSim = 0;
    let skips = 0;
    let archives = 0;
    list.forEach((m) => {
      (m.actions || []).forEach((a) => {
        if (a.type === "skip_case" && a.applied) skips += 1;
        if (a.type === "archive" && a.applied) archives += 1;
        if (a.type === "forward_to") {
          if (a.dry_run) forwardsSim += 1;
          else if (a.sent) forwardsSent += 1;
        }
      });
    });
    return { count: list.length, forwardsSent, forwardsSim, skips, archives };
  };

  const runProcessMessages = async (dryRun) => {
    if (!selectedMessageIds.length) {
      toast.error(t("microsoft.ai_mailboxes.process_need_selection"));
      return;
    }
    if (!dryRun) {
      const ok = window.confirm(t("microsoft.ai_mailboxes.process_run_confirm"));
      if (!ok) return;
    }
    setProcessing(true);
    try {
      const data = await postUserPersonalMailAiProcessMessages({
        graph_message_ids: selectedMessageIds,
        dry_run: dryRun,
      });
      const ip = data?.inbox_processing;
      const s = summarizeProcessing(ip);
      if (dryRun) {
        toast.success(
          t("microsoft.ai_mailboxes.process_ok_simulated", {
            count: s.count,
            sim: s.forwardsSim,
            skips: s.skips,
          })
        );
      } else {
        toast.success(
          t("microsoft.ai_mailboxes.process_ok_executed", {
            count: s.count,
            sent: s.forwardsSent,
            skips: s.skips,
          })
        );
      }
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0] || t("microsoft.ai_mailboxes.process_failed");
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleSendTest = async () => {
    setBusy(true);
    try {
      const data = await postMicrosoftSendTestMail();
      toast.success(data?.message || t("microsoft.test_mail.success"));
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0] || t("microsoft.test_mail.failed");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!microsoftStatus?.can_manage_personal_mail_ai) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("microsoft.user.personal_mail_ai.title")}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {t("microsoft.user.personal_mail_ai.no_permission")}
        </p>
      </div>
    );
  }

  if (!microsoftStatus?.connected) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("microsoft.user.personal_mail_ai.title")}
        </h2>
        <p className="mt-2 text-sm text-gray-600">{t("microsoft.user.personal_mail_ai.connect_first")}</p>
      </div>
    );
  }

  const owaUrl = buildOutlookWebMailboxUrl(mailbox?.email_address || microsoftStatus?.email_address);

  return (
    <>
      <CompanyAiMailboxMessageReaderDialog
        open={Boolean(readerTarget)}
        onClose={() => setReaderTarget(null)}
        variant="personal"
        mailboxId={mailbox?.id}
        mailboxEmail={mailbox?.email_address || microsoftStatus?.email_address}
        graphMessageId={readerTarget?.graphMessageId}
        previewSubject={readerTarget?.subject}
      />
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("microsoft.user.personal_mail_ai.title")}
        </h2>
        <p className="mt-1 text-sm text-gray-600">{t("microsoft.user.personal_mail_ai.intro")}</p>
        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 ring-1 ring-amber-100">
          {t("microsoft.ai_mailboxes.mail_safety_dev_notice")}
        </p>

        {loadingSettings ? (
          <p className="mt-4 text-sm text-gray-500">{t("microsoft.status.loading")}</p>
        ) : mailbox ? (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50/80">
            <div className="flex flex-col gap-2 border-b border-gray-200 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  {mailbox.email_address}
                </p>
                <p className="text-xs text-gray-600">
                  {mailbox.microsoft_connected
                    ? t("microsoft.ai_mailboxes.linked_as", { email: mailbox.email_address })
                    : t("microsoft.ai_mailboxes.not_linked")}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <PMButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busy || !microsoftStatus?.can_send_mail}
                  onClick={handleSendTest}
                >
                  {t("microsoft.ai_mailboxes.send_test")}
                </PMButton>
                {owaUrl ? (
                  <a
                    href={owaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-8 items-center rounded-lg border border-gray-300 bg-white px-3 text-xs font-medium text-gray-800 hover:bg-gray-50"
                  >
                    {t("microsoft.ai_mailboxes.go_to_mailbox")}
                  </a>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-b border-gray-100 px-3 py-2">
              <button
                type="button"
                onClick={toggleInbox}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-gray-800 hover:bg-white"
              >
                {expanded ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
                <EnvelopeOpenIcon className="h-4 w-4 text-gray-500" />
                {t("microsoft.ai_mailboxes.show_inbox")}
              </button>
              <button
                type="button"
                onClick={() => setSettingsOpen((v) => !v)}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-gray-800 hover:bg-white"
              >
                <Cog6ToothIcon className="h-4 w-4 text-gray-500" />
                {t("microsoft.ai_mailboxes.settings.toggle")}
              </button>
            </div>

            {settingsOpen ? (
              <div className="border-b border-gray-100 bg-white px-3 py-4">
                <CompanyAiMailboxSettingsPanel
                  mailbox={mailbox}
                  onSaved={mergeMailbox}
                  patchMailbox={patchUserPersonalMailAiSettings}
                />
              </div>
            ) : null}

            {expanded ? (
              <div className="space-y-3 px-3 py-4">
                {!microsoftStatus?.can_send_mail ? (
                  <p className="text-sm text-amber-800">{t("microsoft.user.personal_mail_ai.no_refresh")}</p>
                ) : (
                  <>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                      <div className="min-w-0 flex-1">
                        <label className="block text-xs font-medium text-gray-700">
                          {t("microsoft.ai_mailboxes.folder.select_label")}
                        </label>
                        <select
                          value={selectedFolderKey}
                          onChange={(ev) => {
                            const v = ev.target.value;
                            setSelectedFolderKey(v);
                            loadMessages(v);
                          }}
                          disabled={loadingFolders}
                          className="mt-1 block w-full max-w-md rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                        >
                          <optgroup label={t("microsoft.ai_mailboxes.folder.group_shortcuts")}>
                            {WELL_KNOWN_MAIL_FOLDER_KEYS.map((k) => (
                              <option key={k} value={k}>
                                {t(`microsoft.ai_mailboxes.folder.seg_${k.replace("wk:", "")}`)}
                              </option>
                            ))}
                          </optgroup>
                          {mailFolders.length > 0 ? (
                            <optgroup label={t("microsoft.ai_mailboxes.folder.group_tree")}>
                              {mailFolders.map((f) => (
                                <option key={`id:${f.id}`} value={`id:${f.id}`}>
                                  {"  ".repeat(Math.min(f.depth || 0, 8))}
                                  {f.display_name}
                                </option>
                              ))}
                            </optgroup>
                          ) : null}
                        </select>
                        {loadingFolders ? (
                          <p className="text-[11px] text-gray-500">
                            {t("microsoft.ai_mailboxes.folder.loading_tree")}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    {loadingMessages ? (
                      <p className="text-sm text-gray-500">{t("microsoft.status.loading")}</p>
                    ) : messages.length === 0 ? (
                      <p className="text-sm text-gray-600">{t("microsoft.ai_mailboxes.no_messages")}</p>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-600">
                          {t("microsoft.ai_mailboxes.process_folder_hint")}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            disabled={busy || processing}
                            onClick={() => selectAllMessages(messages)}
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {t("microsoft.ai_mailboxes.process_select_all")}
                          </button>
                          <button
                            type="button"
                            disabled={busy || processing}
                            onClick={() => setSelectedMessageIds([])}
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {t("microsoft.ai_mailboxes.process_clear_selection")}
                          </button>
                          <PMButton
                            type="button"
                            variant="outlined"
                            loading={processing}
                            disabled={busy || processing || !selectedMessageIds.length}
                            onClick={() => runProcessMessages(true)}
                          >
                            {t("microsoft.ai_mailboxes.process_simulate")}
                          </PMButton>
                          <PMButton
                            type="button"
                            variant="contained"
                            loading={processing}
                            disabled={busy || processing || !selectedMessageIds.length}
                            onClick={() => runProcessMessages(false)}
                          >
                            {t("microsoft.ai_mailboxes.process_run")}
                          </PMButton>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-left text-xs text-gray-800">
                            <thead>
                              <tr className="border-b border-gray-200 text-gray-500">
                                <th className="w-8 py-1 pr-1 font-medium">
                                  <span className="sr-only">
                                    {t("microsoft.ai_mailboxes.process_select_column")}
                                  </span>
                                </th>
                                <th className="py-1 pr-2 font-medium">
                                  {t("microsoft.ai_mailboxes.col.received")}
                                </th>
                                <th className="py-1 pr-2 font-medium">{t("microsoft.ai_mailboxes.col.from")}</th>
                                <th className="py-1 font-medium">{t("microsoft.ai_mailboxes.col.subject")}</th>
                                <th className="w-10 py-1 pl-1 text-right font-medium">
                                  <span className="sr-only">{t("microsoft.ai_mailboxes.reader.open_short")}</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {messages.map((m) => {
                                const sel = selectedMessageIds.includes(m.id);
                                return (
                                  <tr key={m.id} className="border-b border-gray-100">
                                    <td className="py-1.5 pr-1 align-top">
                                      <input
                                        type="checkbox"
                                        checked={sel}
                                        onChange={() => toggleMessageSelected(m.id)}
                                        className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                                        aria-label={t("microsoft.ai_mailboxes.process_select_row")}
                                      />
                                    </td>
                                    <td className="whitespace-nowrap py-1.5 pr-2 align-top">
                                      {formatDate(m.received_at)}
                                    </td>
                                    <td className="max-w-[140px] truncate py-1.5 pr-2 align-top">
                                      {m.from_email || m.from_name || "—"}
                                    </td>
                                    <td className="py-1.5 align-top">
                                      {m.is_read === false && (
                                        <span className="mr-1 inline-block h-2 w-2 rounded-full bg-sky-500 align-middle" />
                                      )}
                                      {m.subject}
                                    </td>
                                    <td className="py-1.5 pl-1 align-top text-right">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setReaderTarget({
                                            graphMessageId: m.id,
                                            subject: m.subject || "",
                                          })
                                        }
                                        className="inline-flex rounded-md border border-sky-200 bg-sky-50 p-1 text-sky-900 hover:bg-sky-100"
                                        title={t("microsoft.ai_mailboxes.reader.open_tooltip")}
                                      >
                                        <EnvelopeOpenIcon className="h-4 w-4" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : null}
          </div>
        ) : (
          <p className="mt-4 text-sm text-gray-600">{t("microsoft.user.personal_mail_ai.load_failed")}</p>
        )}
      </div>
    </>
  );
}
